import { SkeletonPage, ToastProvider } from '@ims/ui'
import { TwoToneIcons } from '@ims/ui/icons'
import { lazy, Suspense } from 'react'
import { AppSkeleton } from './layout/app-skeleton.tsx'
import { createBrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { AppShell } from './layout/shell.tsx'
import { setRouter } from './lib/motion.ts'
import type { AppPlatform } from './platform.ts'
import { AcceptInviteScreen, ResetPasswordScreen, SignInScreen } from './screens/auth.tsx'
import { DashboardScreen } from './screens/dashboard.tsx'
import { InvoiceDetailScreen, InvoicesScreen } from './screens/invoices.tsx'
import { CreateBusinessScreen, StoreGate, TenantPickerScreen } from './screens/onboarding.tsx'
import { CustomersScreen, SuppliersScreen } from './screens/parties.tsx'
import { CustomerDetailScreen } from './screens/customer-detail.tsx'
import { ProductDetailScreen, ProductsScreen } from './screens/products.tsx'
import {
  PurchaseDetailScreen,
  PurchaseOrderDetailScreen,
  PurchaseOrderEditorScreen,
  PurchaseOrdersScreen,
  PurchasesScreen,
  SupplierDetailScreen,
} from './screens/purchases.tsx'
import { ImportScreen } from './screens/import.tsx'
import { QuickBillScreen, type DocEditor } from './screens/quick-bill.tsx'
import { SalesOrderDetailScreen, SalesOrdersScreen } from './screens/sales-orders.tsx'
import { KitchenScreen, OrderScreen, TablesScreen } from './screens/restaurant.tsx'
import { OpenOrdersScreen, ServiceLayout } from './screens/service.tsx'
import { AccountScreen } from './screens/account.tsx'
import { WhatsNewScreen } from './screens/whats-new.tsx'
import { SettingsScreen } from './screens/settings.tsx'
import { PaymentsMadeScreen } from './screens/supplier-bills.tsx'
import { ApprovalsScreen } from './screens/approvals.tsx'
import {
  PurchaseRequestDetailScreen,
  PurchaseRequestEditorScreen,
  PurchaseRequestsScreen,
} from './screens/purchase-requests.tsx'
import { StockScreen } from './screens/stock.tsx'
// Reports are only for managers and pull in more code: loaded when opened
// (the service worker precaches every chunk, so it still opens offline).
const ReportsScreen = lazy(() =>
  import('./screens/reports.tsx').then((m) => ({ default: m.ReportsScreen })),
)
// Activity is for managers too, and reads a week of rows: loaded when opened.
const ActivityScreen = lazy(() =>
  import('./screens/activity.tsx').then((m) => ({ default: m.ActivityScreen })),
)
import { RESET_PASSWORD_PATH, SessionProvider, useSession } from './session.tsx'
import { useBusinessMode } from './hooks.ts'
import { AlertsProvider } from './layout/alerts.tsx'
import { StationShell } from './layout/station-shell.tsx'
import { stationHome } from './lib/station.ts'
import { CounterTabs, StationCounterScreen } from './screens/station-counter.tsx'
import { StationKitchenScreen } from './screens/station-kitchen.tsx'
import { useDeviceStation } from './screens/station-mode.ts'
import { WorkspaceProvider, useRole, useWorkspace } from './workspace.tsx'

// Quick bill as the editor of a quotation (stable objects: they are effect dependencies).
const QUOTE_NEW: DocEditor = { kind: 'quotation', mode: 'new' }
const QUOTE_EDIT: DocEditor = { kind: 'quotation', mode: 'edit' }
const QUOTE_REVISE: DocEditor = { kind: 'quotation', mode: 'revise' }
const ORDER_NEW: DocEditor = { kind: 'order', mode: 'new' }
const ORDER_EDIT: DocEditor = { kind: 'order', mode: 'edit' }
const ORDER_REVISE: DocEditor = { kind: 'order', mode: 'revise' }

/**
 * The pages inside the shell. Restaurant mode adds the Orders page; a waiter has no
 * dashboard, inventory or reports (the server refuses those writes too).
 */
function AppRoutes() {
  const { restaurant, known } = useBusinessMode()
  const waiter = useRole() === 'waiter'
  const home = !waiter ? '/' : restaurant ? '/tables' : '/bill'
  const station = useDeviceStation()
  // A device set to Kitchen or Counter shows only that (docs/plans/stations.md).
  if (restaurant && station === 'kitchen')
    return (
      <Routes>
        <Route element={<StationShell mode="kitchen" />}>
          <Route path="station/kitchen" element={<StationKitchenScreen />} />
        </Route>
        <Route path="*" element={<Navigate to={stationHome('kitchen')} replace />} />
      </Routes>
    )
  if (restaurant && station === 'counter')
    return (
      <AlertsProvider>
        <Routes>
          <Route element={<StationShell mode="counter" left={<CounterTabs />} />}>
            <Route path="station/counter" element={<StationCounterScreen />} />
            <Route path="tables/:orderId" element={<OrderScreen />} />
            <Route
              path="bill"
              element={
                <div className="p-3.5">
                  <QuickBillScreen key="bill" />
                </div>
              }
            />
            <Route
              path="invoices/:id"
              element={
                <div className="p-3.5">
                  <InvoiceDetailScreen />
                </div>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to={stationHome('counter')} replace />} />
        </Routes>
      </AlertsProvider>
    )
  return (
    <Routes>
      <Route element={<AppShell />}>
        {!waiter ? (
          <Route index element={<DashboardScreen />} />
        ) : known ? (
          <Route index element={<Navigate to={home} replace />} />
        ) : (
          // Neither the settings nor the business list say yet whether it is a restaurant.
          <Route index element={<SkeletonPage label="Loading" />} />
        )}
        <Route path="bill" element={<QuickBillScreen key="bill" />} />
        {restaurant && (
          // One Orders page; its three tabs are these addresses.
          <Route element={<ServiceLayout />}>
            <Route path="orders" element={<OpenOrdersScreen />} />
            <Route path="tables" element={<TablesScreen />} />
            <Route path="kitchen" element={<KitchenScreen />} />
          </Route>
        )}
        {restaurant && <Route path="tables/:orderId" element={<OrderScreen />} />}
        <Route path="invoices" element={<InvoicesScreen />} />
        <Route path="invoices/:id" element={<InvoiceDetailScreen />} />
        {!waiter && (
          <>
            <Route
              path="products"
              element={<ProductsScreen key="menu" view={restaurant ? 'menu' : undefined} />}
            />
            {restaurant && (
              <Route
                path="ingredients"
                element={<ProductsScreen key="ingredients" view="ingredients" />}
              />
            )}
            <Route path="products/import" element={<ImportScreen />} />
            <Route path="products/:id" element={<ProductDetailScreen />} />
            <Route path="stock" element={<StockScreen />} />
            <Route
              path="reports"
              element={
                <Suspense fallback={<SkeletonPage label="Loading reports" />}>
                  <ReportsScreen />
                </Suspense>
              }
            />
            <Route
              path="activity"
              element={
                <Suspense fallback={<SkeletonPage label="Loading activity" />}>
                  <ActivityScreen />
                </Suspense>
              }
            />
            <Route path="suppliers" element={<SuppliersScreen />} />
            <Route path="suppliers/:id" element={<SupplierDetailScreen />} />
            <Route path="purchase-orders" element={<PurchaseOrdersScreen />} />
            <Route
              path="purchase-orders/new"
              element={<PurchaseOrderEditorScreen key="po-new" mode="new" />}
            />
            <Route path="purchase-orders/:id" element={<PurchaseOrderDetailScreen />} />
            <Route
              path="purchase-orders/:id/edit"
              element={<PurchaseOrderEditorScreen key="po-edit" mode="edit" />}
            />
            <Route
              path="purchase-orders/:id/revise"
              element={<PurchaseOrderEditorScreen key="po-revise" mode="revise" />}
            />
            <Route path="purchases" element={<PurchasesScreen />} />
            <Route path="purchases/:id" element={<PurchaseDetailScreen />} />
            <Route path="payments-made" element={<PaymentsMadeScreen />} />
            <Route path="approvals" element={<ApprovalsScreen />} />
            <Route path="purchase-requests" element={<PurchaseRequestsScreen />} />
            <Route
              path="purchase-requests/new"
              element={<PurchaseRequestEditorScreen key="req-new" mode="new" />}
            />
            <Route path="purchase-requests/:id" element={<PurchaseRequestDetailScreen />} />
            <Route
              path="purchase-requests/:id/edit"
              element={<PurchaseRequestEditorScreen key="req-edit" mode="edit" />}
            />
            <Route
              path="purchase-requests/:id/copy"
              element={<PurchaseRequestEditorScreen key="req-copy" mode="copy" />}
            />
            {!restaurant && (
              <>
                <Route path="quotations" element={<SalesOrdersScreen kind="quotation" />} />
                <Route
                  path="quotations/new"
                  element={<QuickBillScreen key="quote-new" doc={QUOTE_NEW} />}
                />
                <Route
                  path="quotations/:id"
                  element={<SalesOrderDetailScreen kind="quotation" />}
                />
                <Route
                  path="quotations/:id/edit"
                  element={<QuickBillScreen key="quote-edit" doc={QUOTE_EDIT} />}
                />
                <Route
                  path="quotations/:id/revise"
                  element={<QuickBillScreen key="quote-revise" doc={QUOTE_REVISE} />}
                />
                <Route path="orders" element={<SalesOrdersScreen kind="order" />} />
                <Route
                  path="orders/new"
                  element={<QuickBillScreen key="order-new" doc={ORDER_NEW} />}
                />
                <Route path="orders/:id" element={<SalesOrderDetailScreen kind="order" />} />
                <Route
                  path="orders/:id/edit"
                  element={<QuickBillScreen key="order-edit" doc={ORDER_EDIT} />}
                />
                <Route
                  path="orders/:id/revise"
                  element={<QuickBillScreen key="order-revise" doc={ORDER_REVISE} />}
                />
              </>
            )}
          </>
        )}
        <Route path="customers" element={<CustomersScreen />} />
        <Route path="customers/:id" element={<CustomerDetailScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="account" element={<AccountScreen />} />
        <Route path="whats-new" element={<WhatsNewScreen />} />
        {known && <Route path="*" element={<Navigate to={home} replace />} />}
      </Route>
    </Routes>
  )
}

function WorkspaceGate() {
  const { memberships, membershipsLoaded, current, storeState, selectTenant, refreshMemberships } =
    useWorkspace()
  if (window.location.pathname === '/accept-invite') {
    return (
      <AcceptInviteScreen
        onAccepted={(id) => void refreshMemberships().then(() => selectTenant(id))}
      />
    )
  }
  if (memberships.length === 0)
    return membershipsLoaded ? <CreateBusinessScreen /> : <AppSkeleton />
  if (!current) return <TenantPickerScreen />
  if (storeState.status !== 'ready') return <StoreGate />
  return <AppRoutes />
}

function SessionGate() {
  const { state } = useSession()
  const { pathname } = useLocation()
  if (state.status === 'loading') return <AppSkeleton />
  // Before the workspace opens: the reset link signs in only to set a new password.
  if (pathname === RESET_PASSWORD_PATH) return <ResetPasswordScreen />
  if (state.status === 'signedOut') {
    return window.location.pathname === '/accept-invite' ? (
      <AcceptInviteScreen onAccepted={() => {}} />
    ) : (
      <SignInScreen />
    )
  }
  return (
    <WorkspaceProvider key={state.user.id}>
      <WorkspaceGate />
    </WorkspaceProvider>
  )
}

// A data router (not <BrowserRouter>) only so that a page change can be a view transition
// (docs/plans/app-motion.md). Every path goes to the same tree as before: the screens still
// declare their routes with <Routes>. Links under those <Routes> navigate through history,
// not the router, so `lib/motion.ts` keeps the router and moves through it directly.
// One router for the page's life: made on first use (not in a state initializer, which
// development runs twice and would leave `lib/motion.ts` holding a router not on screen).
let appRouter: ReturnType<typeof createBrowserRouter> | null = null
function getRouter() {
  if (!appRouter) {
    appRouter = createBrowserRouter([{ path: '*', element: <SessionGate /> }])
    setRouter(appRouter)
  }
  return appRouter
}

function AppRouter() {
  return <RouterProvider router={getRouter()} />
}

export function App({ platform }: { platform: AppPlatform }) {
  return (
    <TwoToneIcons>
      <ToastProvider>
        <SessionProvider platform={platform}>
          <AppRouter />
        </SessionProvider>
      </ToastProvider>
    </TwoToneIcons>
  )
}
